// Updated HoverNav.tsx - More robust Popper positioning with refs
import React, { useState, useRef, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  MenuItem as MuiMenuItem,
  Popper,
  Paper,
  Grow,
  ClickAwayListener,
  MenuList,
  Badge,
} from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { BaseNavProps } from '../../types/navigation';

// Safe icon creation
function safeIcon(icon: any) {
  if (!icon) return null;
  if (typeof icon === 'function') {
    return React.createElement(icon, { size: 16 });
  }
  return null;
}

export default function HoverNav({
  navigation,
  userEmail,
  logo,
  UserMenu,
  role,
  onLogout
}: BaseNavProps) {
  const [currentOpenMenu, setCurrentOpenMenu] = useState<string | null>(null);
  const [openSubs, setOpenSubs] = useState<Record<string, boolean>>({});
  const [pendingBandsCount, setPendingBandsCount] = useState(0);

  useEffect(() => {
    if (!['admin', 'gm', 'superadmin'].includes(role)) return;
    const token = localStorage.getItem('token');
    axios.get(`${process.env.REACT_APP_API_URL}/api/admin/bands/pending`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setPendingBandsCount(res.data.length)).catch(() => {});
  }, [role]);
  
  // Store refs for each button
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Convert icons for main / dropdown
  const mainItems = navigation.mainItems.map(item => ({
    ...item,
    icon: safeIcon(item.icon),
    children: item.children?.map(child => ({ ...child, icon: safeIcon(child.icon) }))
  }));
  let dropdowns: Record<string, any[]> | undefined;
  if (navigation.dropdowns) {
    dropdowns = {};
    for (const [section, items] of Object.entries(navigation.dropdowns)) {
      dropdowns[section] = items.map(item => ({
        ...item,
        icon: safeIcon(item.icon),
        children: item.children?.map(child => ({
          ...child,
          icon: safeIcon(child.icon)
        }))
      }));
    }
  }

  // ---------- Parent Hover Handlers ----------
  const handleParentEnter = (section: string) => {
    setCurrentOpenMenu(section);
    setOpenSubs({});
  };
  
  const handleParentLeave = () => {
    setCurrentOpenMenu(null);
    setOpenSubs({});
  };

  // ---------- Submenu Hover Handlers ----------
  const handleSubEnter = (subKey: string) => {
    setOpenSubs(prev => ({ ...prev, [subKey]: true }));
  };
  const handleSubLeave = (subKey: string) => {
    setOpenSubs(prev => ({ ...prev, [subKey]: false }));
  };

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64, px: 2 }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          {logo}
        </Box>

        {/* Nav Items */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Main items — plain links or inline dropdowns if they have children */}
          {mainItems.map(item => {
            if (item.children) {
              const key = item.label;
              return (
                <Box key={key} onMouseEnter={() => handleParentEnter(key)} onMouseLeave={handleParentLeave}>
                  <Button
                    ref={(el) => { buttonRefs.current[key] = el; }}
                    color="inherit"
                    startIcon={item.icon}
                    endIcon={<ChevronDown size={16} />}
                    sx={{ textTransform: 'none' }}
                  >
                    {item.label}
                    {item.label === 'Bands' && pendingBandsCount > 0 && (
                      <Box component="span" sx={{
                        ml: 0.75,
                        bgcolor: 'warning.main',
                        color: 'warning.contrastText',
                        borderRadius: '50%',
                        width: 18,
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {pendingBandsCount}
                      </Box>
                    )}
                  </Button>
                  <Popper
                    open={currentOpenMenu === key}
                    anchorEl={buttonRefs.current[key]}
                    transition
                    placement="bottom-start"
                    sx={{ zIndex: 1300 }}
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                  >
                    {({ TransitionProps }) => (
                      <Grow {...TransitionProps} timeout={200}>
                        <Paper elevation={3} onMouseEnter={() => handleParentEnter(key)} onMouseLeave={handleParentLeave}>
                          <ClickAwayListener onClickAway={handleParentLeave}>
                            <MenuList>
                              {item.children!.map(child => {
                                const subKey = `${key}-${child.label}`;
                                if (child.children) {
                                  return (
                                    <Box key={subKey} sx={{ position: 'relative' }}>
                                      <MuiMenuItem
                                        onMouseEnter={() => handleSubEnter(subKey)}
                                        onMouseLeave={() => handleSubLeave(subKey)}
                                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
                                      >
                                        {child.icon && <Box sx={{ display: 'flex' }}>{child.icon}</Box>}
                                        <Box sx={{ flex: 1 }}>{child.label}</Box>
                                        <ChevronRight size={16} />
                                      </MuiMenuItem>
                                      {openSubs[subKey] && (
                                        <Paper elevation={3} sx={{ position: 'absolute', top: 0, left: '100%', minWidth: 180, zIndex: 1301, ml: 0.5 }}
                                          onMouseEnter={() => handleSubEnter(subKey)} onMouseLeave={() => handleSubLeave(subKey)}>
                                          <MenuList>
                                            {child.children.map((grandchild: any) => (
                                              <MuiMenuItem key={grandchild.path || grandchild.label} component={RouterLink} to={grandchild.path || ''}
                                                onClick={() => { handleSubLeave(subKey); handleParentLeave(); }}>
                                                {grandchild.icon && <Box sx={{ display: 'flex', mr: 1 }}>{grandchild.icon}</Box>}
                                                {grandchild.label}
                                              </MuiMenuItem>
                                            ))}
                                          </MenuList>
                                        </Paper>
                                      )}
                                    </Box>
                                  );
                                }
                                return (
                                  <MuiMenuItem key={child.path || child.label} component={RouterLink} to={child.path || ''} onClick={handleParentLeave}>
                                    {child.icon && <Box sx={{ display: 'flex', mr: 1 }}>{child.icon}</Box>}
                                    {child.label}
                                  </MuiMenuItem>
                                );
                              })}
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </Box>
              );
            }
            return (
              <Button
                key={item.path || item.label}
                component={RouterLink}
                to={item.path || ''}
                color="inherit"
                startIcon={item.icon}
                sx={{ textTransform: 'none' }}
              >
                {item.label}
              </Button>
            );
          })}

          {/* Named dropdown sections */}
          {dropdowns &&
            Object.entries(dropdowns).map(([section, items]) => (
              <Box
                key={section}
                onMouseEnter={() => handleParentEnter(section)}
                onMouseLeave={handleParentLeave}
              >
                <Button
                  ref={(el) => {
                    buttonRefs.current[section] = el;
                  }}
                  color="inherit"
                  endIcon={<ChevronDown size={16} />}
                  sx={{ textTransform: 'none' }}
                >
                  {section}
                </Button>

                {/* PARENT DROPDOWN */}
                <Popper
                  open={currentOpenMenu === section}
                  anchorEl={buttonRefs.current[section]}
                  transition
                  placement="bottom-start"
                  sx={{ zIndex: 1300 }}
                  modifiers={[
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 4],
                      },
                    },
                  ]}
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps} timeout={200}>
                      <Paper 
                        elevation={3}
                        onMouseEnter={() => handleParentEnter(section)}
                        onMouseLeave={handleParentLeave}
                      >
                        <ClickAwayListener onClickAway={handleParentLeave}>
                          <MenuList>
                            {items.map(item => {
                              const subKey = `${section}-${item.label}`;
                              if (item.children) {
                                return (
                                  <Box key={subKey} sx={{ position: 'relative' }}>
                                    {/* Parent Menu Item */}
                                    <MuiMenuItem
                                      onMouseEnter={() => handleSubEnter(subKey)}
                                      onMouseLeave={() => handleSubLeave(subKey)}
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      {item.icon && <Box sx={{ display: 'flex' }}>{item.icon}</Box>}
                                      <Box sx={{ flex: 1 }}>{item.label}</Box>
                                      <ChevronRight size={16} />
                                    </MuiMenuItem>

                                    {/* ABSOLUTE SUBMENU */}
                                    {openSubs[subKey] && (
                                      <Paper
                                        elevation={3}
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: '100%',
                                          minWidth: 180,
                                          zIndex: 1301,
                                          ml: 0.5,
                                        }}
                                        onMouseEnter={() => handleSubEnter(subKey)}
                                        onMouseLeave={() => handleSubLeave(subKey)}
                                      >
                                        <MenuList>
                                          {item.children.map((child: any) => (
                                            <MuiMenuItem
                                              key={child.path || child.label}
                                              component={RouterLink}
                                              to={child.path || ''}
                                              onClick={() => {
                                                handleSubLeave(subKey);
                                                handleParentLeave();
                                              }}
                                            >
                                              {child.icon && <Box sx={{ display: 'flex', mr: 1 }}>{child.icon}</Box>}
                                              {child.label}
                                            </MuiMenuItem>
                                          ))}
                                        </MenuList>
                                      </Paper>
                                    )}
                                  </Box>
                                );
                              }
                              // A simple item
                              return (
                                <MuiMenuItem
                                  key={item.path || item.label}
                                  component={RouterLink}
                                  to={item.path || ''}
                                  onClick={handleParentLeave}
                                >
                                  {item.icon && <Box sx={{ display: 'flex', mr: 1 }}>{item.icon}</Box>}
                                  {item.label}
                                </MuiMenuItem>
                              );
                            })}
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </Box>
            ))}
        </Box>

        {/* User menu - back to the right side */}
        {userEmail && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UserMenu userEmail={userEmail} role={role} onLogout={onLogout} />
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}